import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useEscapeLayer } from '../ui/Dialog';
import './viewers.css';

/**
 * "Kto widzi" (spec 2026-10-23-widocznosc-wydarzen): the accounts on the event's list besides whoever sees all events.
 * One row — "Widzą: Anna, Jan + wszyscy z dostępem do wszystkich wydarzeń" — and a small ⚙ shown on hover (like the
 * quote's gears, #107–#111) that opens the list of users with checkboxes and a search. Escape closes only the list
 * (layers, #96); no ×. The choice is handed over when the list closes (one save, not one per tick).
 */

/** `seesAll`: only for the owner (who else sees all events is the owner's business). */
export type ViewerCandidate = { id: string; name: string; seesAll?: boolean; /** The role cannot view events: shown, not tickable (owner 00:10). */ noAccess?: boolean };

export const VIEWER_CANDIDATES_KEY = ['events', 'viewer-candidates'] as const;

/** Whom the list can name (`GET /api/events/viewer-candidates`, for whoever adds or edits events). */
export function useViewerCandidates(enabled = true) {
  return useQuery({
    queryKey: VIEWER_CANDIDATES_KEY,
    queryFn: () => api<{ users: ViewerCandidate[] }>('/api/events/viewer-candidates').then((r) => r.users),
    enabled,
    staleTime: 60_000,
  });
}

export const ALL_NOTE = '+ wszyscy z dostępem do wszystkich wydarzeń';

/** "Anna, Jan" — the names of the list ("nikt więcej" when empty). */
export const viewersText = (names: readonly string[]): string => (names.length > 0 ? names.join(', ') : 'tylko twórca i obsada');

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .toLowerCase();

/** The ⚙ with the list of users. `onChange(ids)` gets the ticked ids when the list closes and something changed. */
export function ViewersPicker({ value, candidates, onChange, disabled = false }: { value: readonly string[]; candidates: readonly ViewerCandidate[]; onChange: (ids: string[]) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [ticked, setTicked] = useState<string[]>([...value]);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const id = useId();
  const latest = useRef({ ticked, value, onChange });
  latest.current = { ticked, value, onChange };

  const close = (focusButton: boolean) => {
    setOpen(false);
    const { ticked: now, value: before, onChange: hand } = latest.current;
    const same = now.length === before.length && now.every((x) => before.includes(x));
    if (!same) hand(now);
    if (focusButton) button.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    // a press outside closes (never a blur: Safari does not focus pressed buttons, #110)
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) close(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEscapeLayer(() => close(true), open);

  const shown = useMemo(() => {
    const q = fold(query.trim());
    return candidates.filter((c) => q === '' || fold(c.name).includes(q));
  }, [candidates, query]);

  return (
    <div className="viewers-gear" ref={root}>
      <button
        ref={button}
        type="button"
        className="btn icon"
        data-icon="gear"
        title="Zmień, kto widzi wydarzenie"
        aria-label="Zmień, kto widzi wydarzenie"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        disabled={disabled}
        onClick={() => {
          if (open) close(false);
          else {
            setTicked([...value]);
            setQuery('');
            setOpen(true);
          }
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      {open && (
        <div className="viewers-pop" id={id} role="dialog" aria-label="Kto widzi wydarzenie">
          <input ref={search} type="search" className="viewers-search" placeholder="Szukaj osoby…" aria-label="Szukaj osoby" value={query} onChange={(e) => setQuery(e.target.value)} />
          <ul className="viewers-list">
            {shown.map((c) => (
              <li key={c.id}>
                <label className="check">
                  <input
                    type="checkbox"
                    disabled={c.noAccess && !ticked.includes(c.id)}
                    checked={ticked.includes(c.id)}
                    onChange={(e) => setTicked((now) => (e.target.checked ? [...now, c.id] : now.filter((x) => x !== c.id)))}
                  />
                  <span>
                    {c.name}
                    {c.seesAll && <small className="muted"> — widzi wszystkie</small>}
                    {c.noAccess && <small className="muted"> — jego rola nie ma podglądu wydarzeń (zmień w Użytkownikach)</small>}
                  </span>
                </label>
              </li>
            ))}
            {shown.length === 0 && <li className="muted">Nikt nie pasuje</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Who sees all events anyway (the owner, "Domyślnie widzi wszystkie wydarzenia"), not on the list. */
export const seeingAllText = (candidates: readonly ViewerCandidate[], listed: readonly string[]): string =>
  candidates
    .filter((c) => c.seesAll && !listed.includes(c.id))
    .map((c) => c.name)
    .join(', ');

/** The row of the "Szczegóły" tab: `<dt>Widzą</dt>` and the names; ⚙ only with `onChange` (events edit right). */
export function ViewersRow({ ids, names, onChange, busy = false }: { ids: readonly string[]; names: readonly string[]; onChange?: ((ids: string[]) => void) | undefined; busy?: boolean }) {
  const candidates = useViewerCandidates(!!onChange);
  return (
    <div className="viewers-row" data-feature-id="events.viewers">
      <dt className="muted">Widzą</dt>
      <dd>
        {onChange && <ViewersPicker value={ids} candidates={candidates.data ?? []} onChange={onChange} disabled={busy || !candidates.data} />}
        <span>{viewersText(names)}</span> <small className="muted">{ALL_NOTE}</small>
      </dd>
    </div>
  );
}

/** The field of the forms that create an event or a rental: by default the creator alone; who else sees it through the flag. */
export function ViewersFormField({ value, onChange }: { value: readonly string[]; onChange: (ids: string[]) => void }) {
  const candidates = useViewerCandidates();
  const list = candidates.data ?? [];
  const names = value.map((id) => list.find((c) => c.id === id)?.name).filter((n): n is string => !!n);
  const others = seeingAllText(list, value);
  return (
    <div className="field viewers-field" data-feature-id="events.viewers">
      <span>Widzą</span>
      <div className="viewers-value">
        <ViewersPicker value={value} candidates={list} onChange={onChange} disabled={!candidates.data} />
        <span>{viewersText(names)}</span>
      </div>
      <small className="hint">{others ? `Zobaczą je też (widzą wszystkie wydarzenia): ${others}.` : ALL_NOTE}</small>
    </div>
  );
}
